/**
 * Password validation utility for user-friendly password requirements
 *
 * Requirements:
 * - Minimum 8 characters
 * - At least one letter (uppercase or lowercase)
 * - At least one number OR special character
 *
 * This is much more lenient than the previous policy which required:
 * uppercase AND lowercase AND number AND special character.
 *
 * Now most passwords from password managers like 1Password, LastPass, etc. will work.
 */

export interface PasswordValidationResult {
  checks: {
    minLength: boolean;
    hasLetter: boolean;
    hasNumberOrSymbol: boolean;
  };
  isValid: boolean;
}

/**
 * Validates a password against user-friendly requirements
 * @param password - The password to validate
 * @returns Validation result with individual checks and overall validity
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  const checks = {
    minLength: password.length >= 8,
    hasLetter: /[a-zA-Z]/.test(password),
    // Accept any number or common special character (very permissive)
    // eslint-disable-next-line no-useless-escape
    hasNumberOrSymbol: /[\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password),
  };

  const isValid = checks.minLength && checks.hasLetter && checks.hasNumberOrSymbol;

  return { checks, isValid };
};

/**
 * Get a user-friendly error message for password validation
 * @param password - The password that was validated
 * @returns Error message or null if password is valid
 */
export const getPasswordErrorMessage = (password: string): string | null => {
  const validation = validatePassword(password);

  if (validation.isValid) {
    return null;
  }

  if (!validation.checks.minLength) {
    return 'Password must be at least 8 characters long';
  }

  if (!validation.checks.hasLetter) {
    return 'Password must contain at least one letter';
  }

  if (!validation.checks.hasNumberOrSymbol) {
    return 'Password must contain at least one number or special character';
  }

  return 'Password does not meet requirements';
};

/**
 * Password requirements for display in UI
 */
export const PASSWORD_REQUIREMENTS = [
  {
    id: 'minLength',
    text: 'At least 8 characters',
    check: (password: string) => password.length >= 8,
  },
  {
    id: 'hasLetter',
    text: 'Contains at least one letter',
    check: (password: string) => /[a-zA-Z]/.test(password),
  },
  {
    id: 'hasNumberOrSymbol',
    text: 'Contains at least one number or special character',
    // eslint-disable-next-line no-useless-escape
    check: (password: string) => /[\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password),
  },
];

/**
 * Helper text for password fields
 */
export const PASSWORD_HELPER_TEXT =
  '💡 Tip: Most passwords generated by 1Password, LastPass, or other password managers will work!';
